/**
 * review360Aggregator.ts
 *
 * Anonymity-aware aggregation of Review360Submissions. This module is the
 * SINGLE chokepoint that enforces CLAUDE.md §D.1:
 *   - 'attributed' relationships: reviewer identity may be returned
 *   - 'anonymous' relationships: identity stripped; per-relationship
 *     aggregates only exposed when reviewer count ≥ MIN_REVIEWERS_FOR_EXPOSURE
 *
 * Callers must NEVER bypass this aggregator when serving non-HR roles.
 */

import {
  COMPANY_VALUE_KEYS, MIN_REVIEWERS_FOR_EXPOSURE,
  type CompanyValueKey, type Review360Submission,
  type Review360Relationship,
} from '../types/eprFramework';
import type { Review360Summary } from '../types/appraisal';
import { listSubmissionsForReviewee } from './review360SubmissionService';

export type RequesterRole = 'hr' | 'manager' | 'employee';

export interface Aggregator360Options {
  /** Override min-reviewers for HR previews / tests. Default: 3 per §D.1. */
  minReviewers?: number;
}

/**
 * Produce the appraisal-facing summary for an employee under a given
 * requesting role.
 *
 * - HR: sees everything (full audit; identity-bearing for attributed).
 *       Aggregates without threshold (HR is privileged).
 * - Manager / Employee: anonymous submissions are aggregated only when the
 *   count of anonymous reviewers in that relationship is ≥ minReviewers.
 *   Below threshold: scores omitted; free-text themes still surfaced.
 */
export async function summarize360ForReviewee(
  cycleId: string,
  revieweeId: string,
  requester: RequesterRole,
  opts: Aggregator360Options = {},
): Promise<Review360Summary> {
  const subs = await listSubmissionsForReviewee(cycleId, revieweeId);
  return summarizeFromSubmissions(subs, requester, opts);
}

/**
 * Pure summary builder. Exposed for testing / re-use when the caller
 * already has the submissions in hand.
 */
export function summarizeFromSubmissions(
  subs: Review360Submission[],
  requester: RequesterRole,
  opts: Aggregator360Options = {},
): Review360Summary {
  const minReviewers = opts.minReviewers ?? MIN_REVIEWERS_FOR_EXPOSURE;
  const isPrivileged = requester === 'hr';

  // Decide which submissions contribute SCORES vs only TEXT
  // For privileged callers, all submissions contribute scores.
  // For non-privileged: anonymous submissions contribute scores only when
  //   their per-relationship count ≥ minReviewers. Attributed always contribute.
  const anonymousByRelationship = new Map<Review360Relationship, Review360Submission[]>();
  for (const s of subs) {
    if (s.effectiveVisibility === 'anonymous') {
      const list = anonymousByRelationship.get(s.relationship) ?? [];
      list.push(s);
      anonymousByRelationship.set(s.relationship, list);
    }
  }
  const anonymousScoringRelationships = new Set<Review360Relationship>();
  if (isPrivileged) {
    for (const rel of anonymousByRelationship.keys()) anonymousScoringRelationships.add(rel);
  } else {
    for (const [rel, list] of anonymousByRelationship.entries()) {
      if (list.length >= minReviewers) anonymousScoringRelationships.add(rel);
    }
  }

  const scoringSubs = subs.filter(s => {
    if (s.effectiveVisibility === 'attributed') return true;
    return anonymousScoringRelationships.has(s.relationship);
  });

  const valueAverages: Partial<Record<CompanyValueKey, number>> = {};
  for (const key of COMPANY_VALUE_KEYS) {
    const samples = scoringSubs
      .map(s => s.valueScores[key])
      .filter((x): x is 1 | 2 | 3 | 4 | 5 => typeof x === 'number');
    if (samples.length === 0) continue;
    valueAverages[key] = samples.reduce((a, b) => a + b, 0) / samples.length;
  }
  const overallAverage = Object.values(valueAverages).length > 0
    ? Object.values(valueAverages).reduce<number>((a, b) => a + b, 0)
      / Object.values(valueAverages).length
    : undefined;

  const strengths        = collectText(subs, 'strengths');
  const thingsToImprove  = collectText(subs, 'thingsToImprove');
  const overallComments  = collectText(subs, 'overallComments');

  return {
    reviewerCount: subs.length,
    valueAverages,
    overallAverage,
    strengths,
    thingsToImprove,
    overallComments,
  };
}

function collectText(
  subs: Review360Submission[],
  field: 'strengths' | 'thingsToImprove' | 'overallComments',
): string {
  return subs
    .map(s => s[field]?.trim())
    .filter((t): t is string => !!t)
    .join('\n\n— — —\n\n');
}

/**
 * Build a "who reviewed" roster suitable for HR audit views only. Returns
 * identity for attributed; only relationship counts for anonymous.
 * Throws if called with a non-HR requester.
 */
export interface ReviewerRosterRow {
  visibility: 'attributed' | 'anonymous';
  relationship: Review360Relationship;
  reviewerId?: string;
  reviewerName?: string;
}

export function buildReviewerRoster(
  subs: Review360Submission[],
  requester: RequesterRole,
): ReviewerRosterRow[] {
  if (requester !== 'hr') {
    // Non-HR cannot see the roster — return anonymised counts disguised as rows
    const counts = new Map<Review360Relationship, number>();
    for (const s of subs) {
      counts.set(s.relationship, (counts.get(s.relationship) ?? 0) + 1);
    }
    const rows: ReviewerRosterRow[] = [];
    for (const [rel, count] of counts.entries()) {
      for (let i = 0; i < count; i++) {
        rows.push({ visibility: 'anonymous', relationship: rel });
      }
    }
    return rows;
  }
  return subs.map(s => ({
    visibility:   s.effectiveVisibility,
    relationship: s.relationship,
    reviewerId:   s.reviewerId,
    reviewerName: s.reviewerName,
  }));
}
